/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

// HomeModelImpl grew its public/private members interleaved in this fork;
// reordering the whole class to satisfy the rule would be high-churn, risky
// refactoring for no behavior benefit. New members should still follow
// public-before-private.

import { ClientScope } from "app/client/components/ClientScope";
import { guessTimezone } from "app/client/lib/guessTimezone";
import { HomePluginManager } from "app/client/lib/HomePluginManager";
import { ImportSourceElement } from "app/client/lib/ImportSourceElement";
import { isKnetEmbed } from "app/client/lib/knetEmbed";
import { localStorageJsonObs, localStorageObs } from "app/client/lib/localStorageObs";
import { AppModel, reportError } from "app/client/models/AppModel";
import { reportMessage, UserError } from "app/client/models/errors";
import { urlState } from "app/client/models/gristUrlState";
import { ownerName } from "app/client/models/WorkspaceInfo";
import { IHomePage, isFeatureEnabled } from "app/common/gristUrls";
import { isLongerThan } from "app/common/gutil";
import { LAST_WORKSPACE_KEY, lastUsedWorkspace, recordWorkspace, selectWorkspace } from "app/common/knetWorkspace";
import { SortPref, UserOrgPrefs, ViewPref } from "app/common/Prefs";
import * as roles from "app/common/roles";
import { getGristConfig } from "app/common/urlUtils";
import {
  AperagApiKey, AperagBot, AperagCollection, ConnectAppConfig, ConnectLinkedAccount,
  ConnectProject, Crawler,
  Document, GisProject,
  KnetAgent, KnetFlow, Organization, RenameDocOptions, Workspace,
} from "app/common/UserAPI";

import { bundleChanges, Computed, Disposable, Observable, subscribe } from "grainjs";
import flatten from "lodash/flatten";
import sortBy from "lodash/sortBy";

const DELAY_BEFORE_SPINNER_MS = 500;

// KNet: skip the Phase-2 external-service fetches in updateWorkspaces().
// Those calls hit aperag, ACI, knetai, mundi and krawler on every home page
// load just to render Online/Sleeping status and live counts — waking each
// Knative service from scale-to-zero. With this flag on, Mission Control runs
// off the localStorage cache and services wake only when the user opens them.
// Set to false to restore live status dots and fresh counts.
const SKIP_EXTERNAL_SERVICE_FETCHES = true;

/** Shape stored in localStorage for each user+org combination. */
interface HomeDataCache {
  fetchedAt: string;           // ISO timestamp
  knetAgents: KnetAgent[];
  aperagCollections: AperagCollection[];
  connectAppConfigs: ConnectAppConfig[];
  connectLinkedAccounts: ConnectLinkedAccount[];
  gisProjects: GisProject[];
  aperagBots: AperagBot[];
  crawlers: Crawler[];
}

export interface HomeModel {
  // PageType value, one of the discriminated union values used by AppModel.
  pageType: "home";

  app: AppModel;
  currentPage: Observable<IHomePage>;
  currentWSId: Observable<number | undefined>;    // should be set when currentPage is 'workspace'

  // Note that Workspace contains its documents in .docs.
  workspaces: Observable<Workspace[]>;
  loading: Observable<boolean | "slow">;          // Set to "slow" when loading for a while.
  available: Observable<boolean>;               // set if workspaces loaded correctly.
  empty: Observable<boolean>;                   // set if no docs.
  singleWorkspace: Observable<boolean>;         // set if workspace name should be hidden.
  trashWorkspaces: Observable<Workspace[]>;     // only set when viewing trash
  templateWorkspaces: Observable<Workspace[]>;  // Only set when viewing templates or all documents.

  // currentWS is undefined when currentPage is not "workspace" or if currentWSId doesn't exist.
  currentWS: Observable<Workspace | undefined>;

  // The one selected workspace, never "all": the URL's, else the site's last used, else
  // Home, else the first (hermes-agent-ui openspec one-selected-workspace).
  selectedWS: Observable<Workspace | undefined>;

  // List of docs to show for currentWS.
  currentWSDocs: Observable<HomeItem[]>;

  // List of featured templates from templateWorkspaces.
  featuredTemplates: Observable<Document[]>;

  // List of other sites (orgs) user can access. Only populated on All Documents, and only when
  // the current org is a personal org, or the current org is view access only.
  otherSites: Observable<Organization[]>;

  onlyShowDocuments: Observable<boolean>;
  workspaceFilter: Observable<number | null>;

  currentSort: Observable<SortPref>;
  currentView: Observable<ViewPref>;
  importSources: Observable<ImportSourceElement[]>;

  // The workspace for new docs, or "unsaved" to only allow unsaved-doc creation, or null if the
  // user isn't allowed to create a doc.
  newDocWorkspace: Observable<Workspace | null | "unsaved">;

  shouldShowAddNewTip: Observable<boolean>;

  onboardingTutorial: Observable<Document | null>;
  aperagBots: Observable<AperagBot[]>;
  aperagCollections: Observable<AperagCollection[]>;
  aperagApiKeys: Observable<AperagApiKey[]>;
  configuredAppConnectors: Observable<string[]>;
  connectProjects: Observable<ConnectProject[]>;
  connectAppConfigs: Observable<ConnectAppConfig[]>;
  connectLinkedAccounts: Observable<ConnectLinkedAccount[]>;
  knetAgents: Observable<KnetAgent[]>;
  knetFlows: Observable<KnetFlow[]>;
  gisProjects: Observable<GisProject[]>;
  crawlers: Observable<Crawler[]>;

  // Service health status observables
  aperagBotsOnline: Observable<boolean>;
  aperagCollectionsOnline: Observable<boolean>;
  knetAgentsOnline: Observable<boolean>;
  knetFlowsOnline: Observable<boolean>;
  gisProjectsOnline: Observable<boolean>;
  configuredAppsOnline: Observable<boolean>;
  crawlersOnline: Observable<boolean>;

  // ISO timestamp of last successful full fetch — null if never fetched in this browser.
  lastFetchedAt: Observable<string | null>;

  // The real org ID, resolving the merged "docs" org (id=0) via the selected_org_id cookie.
  effectiveOrgId: number;

  createWorkspace(name: string): Promise<void>;
  renameWorkspace(id: number, name: string): Promise<void>;
  deleteWorkspace(id: number, forever: boolean): Promise<void>;
  restoreWorkspace(ws: Workspace): Promise<void>;

  createDoc(name: string, workspaceId: number | "unsaved"): Promise<string>;
  renameDoc(docId: string, name: string, options?: RenameDocOptions): Promise<void>;
  deleteDoc(docId: string, forever: boolean): Promise<void>;
  restoreDoc(doc: Document): Promise<void>;
  pinUnpinDoc(docId: string, pin: boolean): Promise<void>;
  pinUnpinItem(item: HomeItem, pin: boolean): Promise<void>;
  moveDoc(docId: string, workspaceId: number): Promise<void>;
  updateWorkspaces(): Promise<void>;
}

export interface HomeItem {
  id: string;
  name: string;
  updatedAt: string;
  removedAt?: string;
  isPinned?: boolean;
  public?: boolean;
  access: roles.Role;
  workspaceName: string;
  icon?: string;
  type: "doc" | "bot" | "collection" | "connector" | "connect-agent" | "connect-app" | "connect-account" |
    "knet-workspace" | "knet-agent" | "knet-flow" | "gis-project" | "crawler";
  href?: string;
  doc?: Document; // Original doc if type is 'doc'
}

export interface ViewSettings {
  currentSort: Observable<SortPref>;
  currentView: Observable<ViewPref>;
}

export class HomeModelImpl extends Disposable implements HomeModel, ViewSettings {
  public readonly pageType = "home";
  public readonly currentPage = Computed.create(this, urlState().state, (use, s) =>
    s.homePage || (s.ws !== undefined ? "workspace" : "all"));

  public readonly currentWSId = Computed.create(this, urlState().state, (use, s) => s.ws);

  // The real org ID — for the merged "docs" org (id=0) falls back to the selected_org_id cookie.
  public readonly effectiveOrgId: number = (() => {
    const orgId = this._app.currentOrg?.id ?? 0;
    return orgId || (_getSelectedOrgIdFromCookie() ?? orgId);
  })();

  public readonly workspaces = Observable.create<Workspace[]>(this, []);
  public readonly workspaceFilter = Observable.create<number | null>(this, null);
  public readonly loading = Observable.create<boolean | "slow">(this, true);
  public readonly available = Observable.create(this, false);
  public readonly singleWorkspace = Observable.create(this, true);
  public readonly trashWorkspaces = Observable.create<Workspace[]>(this, []);
  public readonly templateWorkspaces = Observable.create<Workspace[]>(this, []);
  public readonly importSources = Observable.create<ImportSourceElement[]>(this, []);
  public readonly aperagBots = Observable.create<AperagBot[]>(this, []);
  public readonly aperagCollections = Observable.create<AperagCollection[]>(this, []);
  public readonly aperagApiKeys = Observable.create<AperagApiKey[]>(this, []);
  public readonly configuredAppConnectors = Observable.create<string[]>(this, []);
  public readonly connectProjects = Observable.create<ConnectProject[]>(this, []);
  public readonly connectAppConfigs = Observable.create<ConnectAppConfig[]>(this, []);
  public readonly connectLinkedAccounts = Observable.create<ConnectLinkedAccount[]>(this, []);
  public readonly knetAgents = Observable.create<KnetAgent[]>(this, []);
  public readonly knetFlows = Observable.create<KnetFlow[]>(this, []);
  public readonly gisProjects = Observable.create<GisProject[]>(this, []);
  public readonly crawlers = Observable.create<Crawler[]>(this, []);

  // Get the workspace details for the workspace with id of currentWSId.
  public readonly currentWS = Computed.create(this, use =>
    use(this.workspaces).find(ws => (ws.id === use(this.currentWSId))));

  public readonly selectedWS = Computed.create(this, use =>
    selectWorkspace(
      use(this.currentWSId),
      lastUsedWorkspace(_readStorage(LAST_WORKSPACE_KEY), this.effectiveOrgId),
      use(this.workspaces).filter(ws => !ws.isSupportWorkspace),
    ));

  public readonly onlyShowDocuments = Observable.create<boolean>(this, true);

  // User org preferences - must be defined before currentWSDocs which uses it
  private _userOrgPrefs = Observable.create<UserOrgPrefs | undefined>(this, this._app.currentOrg?.userOrgPrefs);

  // Track service health to avoid cleaning up pinned items when services are down
  private _serviceHealth: {
    aperagBotsOnline: boolean;
    aperagCollectionsOnline: boolean;
    knetAgentsOnline: boolean;
    knetFlowsOnline: boolean;
    gisProjectsOnline: boolean;
    crawlersOnline: boolean;
  } = {
    aperagBotsOnline: true,
    aperagCollectionsOnline: true,
    knetAgentsOnline: true,
    knetFlowsOnline: true,
    gisProjectsOnline: true,
    crawlersOnline: true,
  };

  // Public observables for UI to display service status
  public readonly aperagBotsOnline = Observable.create(this, true);
  public readonly aperagCollectionsOnline = Observable.create(this, true);
  public readonly knetAgentsOnline = Observable.create(this, true);
  public readonly knetFlowsOnline = Observable.create(this, true);
  public readonly gisProjectsOnline = Observable.create(this, true);
  public readonly configuredAppsOnline = Observable.create(this, true);
  public readonly crawlersOnline = Observable.create(this, true);

  // localStorage-backed cache of service data + timestamp, scoped per user+org.
  private readonly _dataCache: Observable<HomeDataCache | null> = (() => {
    const userId = this._app.currentValidUser?.id ?? 0;
    const orgId  = this._app.currentOrg?.id ?? 0;
    return localStorageJsonObs<HomeDataCache | null>(`home:cache:u=${userId}:o=${orgId}`, null);
  })();

  // Derived observable: just the timestamp from the cache, exposed on the interface.
  public readonly lastFetchedAt: Observable<string | null> = Computed.create(
    this, this._dataCache, (_use, cache) => cache?.fetchedAt ?? null,
  );

  public readonly currentWSDocs = Computed.create(
    this,
    (use): HomeItem[] => {
      const page = use(this.currentPage);
      const workspaces = use(this.workspaces);
      const ws = use(this.currentWS);
      const wsFilter = use(this.workspaceFilter);
      const bots = use(this.aperagBots);
      const collections = use(this.aperagCollections);
      const connectors = use(this.configuredAppConnectors);
      // Connect resources
      const connectProjects = use(this.connectProjects);
      const connectApps = use(this.connectAppConfigs);
      const connectAccounts = use(this.connectLinkedAccounts);
      const knetAgents = use(this.knetAgents);
      const knetFlows = use(this.knetFlows);
      const gisProjects = use(this.gisProjects);
      const crawlers = use(this.crawlers);

      // Get pinned items from UserOrgPrefs
      const userOrgPrefs = use(this._userOrgPrefs);
      const pinnedItems = userOrgPrefs?.pinnedItems || {};
      const pinnedBots = new Set(pinnedItems.bots || []);
      const pinnedCollections = new Set(pinnedItems.collections || []);
      const pinnedKnetAgents = new Set(pinnedItems.knetAgents || []);
      const pinnedKnetFlows = new Set(pinnedItems.knetFlows || []);
      const pinnedGisProjects = new Set(pinnedItems.gisProjects || []);

      const items: HomeItem[] = [];

      // Build a set of "starter" workspace IDs to exclude agents and flows from those workspaces.
      const isStarterFolder = (name: string) => name.toLowerCase().includes("starter");
      const starterWorkspaceIds = new Set<number>();
      // Build a set of ALL workspace IDs belonging to this org — used to filter out
      // cross-user leaked items from the ACI backend's org-level linked-accounts response.
      const orgWorkspaceIds = new Set<number>();
      for (const w of workspaces) {
        orgWorkspaceIds.add(w.id);
        if (isStarterFolder(w.name)) {
          starterWorkspaceIds.add(w.id);
        }
      }

      // Unify "all" overview and single-workspace view: use an effective filter.
      // When viewing a specific workspace (ws), scope everything to that workspace.
      // When using the workspace dropdown filter, scope to wsFilter.
      const effectiveFilter: number | null = ws ? ws.id : wsFilter;

      if (page === "all" || ws) {
        // Add actual documents — filtered to one workspace when effectiveFilter is set
        for (const w of workspaces) {
          if (w.isSupportWorkspace) { continue; }
          if (isStarterFolder(w.name)) { continue; }
          if (effectiveFilter !== null && w.id !== effectiveFilter) { continue; }
          for (const d of w.docs) {
            items.push({
              id: d.id,
              name: d.name,
              updatedAt: d.updatedAt,
              removedAt: d.removedAt,
              isPinned: d.isPinned,
              public: d.public,
              access: d.access,
              workspaceName: "Database",
              type: "doc",
              doc: d,
            });
          }
        }

        // Add external resources if not suppressed
        if (!use(this.onlyShowDocuments)) {
          for (const bot of bots) {
            if (effectiveFilter !== null && bot.workspace_id !== effectiveFilter) { continue; }
            items.push({
              id: bot.id,
              name: bot.title || bot.id,
              updatedAt: bot.updated || bot.created || "",
              isPinned: pinnedBots.has(bot.id),
              access: "owners",
              workspaceName: "Collection Role",
              icon: "BotRole",
              type: "bot",
              href: `/knowledgebases/o/${this.effectiveOrgId}/ws/${bot.workspace_id || effectiveFilter || workspaces[0]?.id || 1}/bots/${bot.id}/settings`,
            });
          }
          for (const coll of collections) {
            if (effectiveFilter !== null && coll.workspace_id !== effectiveFilter) { continue; }
            items.push({
              id: coll.id,
              name: coll.title || coll.id,
              updatedAt: coll.updated || coll.created || "",
              isPinned: pinnedCollections.has(coll.id),
              access: "owners",
              workspaceName: "Collection",
              icon: "Book",
              type: "collection",
              href: `/knowledgebases/o/${this.effectiveOrgId}/ws/${coll.workspace_id || effectiveFilter || workspaces[0]?.id || 1}/collections/${coll.id}/documents`,
            });
          }
          // Connectors are plain strings with no workspace association — hide when filter active.
          if (effectiveFilter === null) {
            for (const conn of connectors) {
              items.push({
                id: conn,
                name: conn,
                updatedAt: "",
                access: "owners",
                workspaceName: "Configured App",
                icon: "Layers",
                type: "connector",
                href: `/connectors/available/${conn}`,
              });
            }
          }
          // Add Connect resources — filter by project workspace_id when available.
          for (const proj of connectProjects) {
            if (effectiveFilter !== null && proj.workspace_id !== undefined && proj.workspace_id !== effectiveFilter) {
              continue;
            }
            for (const agent of proj.agents) {
              const wsId = proj.workspace_id || effectiveFilter || (workspaces[0]?.id || 1);
              items.push({
                id: agent.id,
                name: agent.name,
                updatedAt: "",
                access: "owners",
                workspaceName: "Tool Role",
                icon: "BotRole",
                type: "connect-agent",
                href: `/connectors/o/${this.effectiveOrgId}/ws/${wsId}/agent/${agent.id}`,
              });
            }
          }
          for (const app of connectApps) {
            // Guard: skip items whose workspace_id doesn't belong to the current org
            // (ACI org-level endpoint may return items from other orgs).
            if (app.workspace_id != null && !orgWorkspaceIds.has(app.workspace_id)) { continue; }
            if (effectiveFilter !== null && app.workspace_id !== effectiveFilter) { continue; }
            const wsId = app.workspace_id || effectiveFilter || (workspaces[0]?.id || 1);
            const appName = _formatAppName(app.app_name) || app.id;
            items.push({
              id: app.id,
              name: appName,
              updatedAt: app.updated_at || app.created_at,
              access: "owners",
              workspaceName: "App Configuration",
              icon: "Layers",
              type: "connect-app",
              href: `/connectors/o/${this.effectiveOrgId}/ws/${wsId}/appconfigs/${app.app_name}`,
            });
          }
          for (const acc of connectAccounts) {
            // Guard: skip items whose workspace_id doesn't belong to the current org.
            if (acc.workspace_id != null && !orgWorkspaceIds.has(acc.workspace_id)) { continue; }
            if (effectiveFilter !== null && acc.workspace_id !== effectiveFilter) { continue; }
            const wsId = acc.workspace_id || effectiveFilter || (workspaces[0]?.id || 1);
            const accOwner = acc.linked_account_owner_id || _formatAppName(acc.app_name) || acc.id;
            const accWs = workspaces.find(w => w.id === acc.workspace_id);
            const accName = accWs ? `${accOwner} (${accWs.name})` : accOwner;
            items.push({
              id: acc.id,
              name: accName,
              updatedAt: acc.updated_at || acc.created_at,
              access: "owners",
              workspaceName: "Linked Account",
              icon: "BotRole",
              type: "connect-account",
              href: `/connectors/o/${this.effectiveOrgId}/ws/${wsId}/connected`,
            });
          }
          // Add Knet resources — agents are filtered via direct workspace_id.
          if (Array.isArray(knetAgents)) {
            for (const agent of knetAgents) {
              const agentId = agent.id || agent.uuid || agent.knet_id;
              if (agent.workspace_id !== undefined && starterWorkspaceIds.has(agent.workspace_id)) { continue; }
              if (effectiveFilter !== null && agent.workspace_id !== effectiveFilter) { continue; }
              const wsId = agent.workspace_id || effectiveFilter || (workspaces[0]?.id || 1);
              items.push({
                id: agentId || "unknown",
                name: agent.name,
                updatedAt: agent.updated_at,
                isPinned: agentId ? pinnedKnetAgents.has(agentId) : false,
                access: "owners",
                workspaceName: "Knet Agent",
                icon: "Users2",
                type: "knet-agent",
                href: `/flows/o/${this.effectiveOrgId}/ws/${wsId}/knet/${agentId}`,
              });
            }
          }

          if (Array.isArray(gisProjects)) {
            for (const map of gisProjects) {
              if (effectiveFilter !== null && map.workspace_id !== effectiveFilter) { continue; }
              const wsId = map.workspace_id || effectiveFilter || (workspaces[0]?.id || 1);
              items.push({
                id: map.id,
                name: map.title,
                updatedAt: map.most_recent_version?.last_edited || map.created_on,
                isPinned: pinnedGisProjects.has(map.id),
                access: "owners",
                workspaceName: "GIS Map",
                icon: "World",
                type: "gis-project",
                href: `/maps/o/${this.effectiveOrgId}/ws/${wsId}/project/${map.id}`,
              });
            }
          }
          if (Array.isArray(knetFlows)) {
            for (const flow of knetFlows) {
              const flowId = flow.id || flow.uuid;
              if (flow.workspace_id !== undefined && starterWorkspaceIds.has(flow.workspace_id)) { continue; }
              if (effectiveFilter !== null && flow.workspace_id !== effectiveFilter) { continue; }
              const wsId = flow.workspace_id || effectiveFilter || (workspaces[0]?.id || 1);
              items.push({
                id: flowId || "unknown",
                name: flow.name,
                updatedAt: flow.updated_at,
                isPinned: flowId ? pinnedKnetFlows.has(flowId) : false,
                access: "owners",
                workspaceName: "Knet Flow",
                icon: "Workflow",
                type: "knet-flow",
                href: `/flows/o/${this.effectiveOrgId}/ws/${wsId}/flow/${flowId}`,
              });
            }
          }
          if (Array.isArray(crawlers)) {
            for (const crawler of crawlers) {
              if (effectiveFilter !== null && crawler.workspace_id !== effectiveFilter) { continue; }
              const wsId = crawler.workspace_id || effectiveFilter || (workspaces[0]?.id || 1);
              items.push({
                id: crawler.id,
                name: crawler.name,
                updatedAt: crawler.gmt_updated || "",
                isPinned: false, // add pinning if needed
                access: "owners",
                workspaceName: "Crawler",
                icon: "Link", // will check icon
                type: "crawler",
                href: `/crawlers/o/${this.effectiveOrgId}/ws/${wsId}/crawlers/${crawler.id}`,
              });
            }
          }
        }
      }

      // Cleanup: Remove pinned IDs that no longer exist in the backend
      // This gracefully handles when items are deleted from backend services
      this._cleanupStalePinnedItems(
        pinnedBots,
        pinnedCollections,
        pinnedKnetAgents,
        pinnedKnetFlows,
        pinnedGisProjects,
        bots,
        collections,
        knetAgents,
        knetFlows,
        gisProjects,
        userOrgPrefs,
      );

      return items;
    },
  );

  public readonly featuredTemplates = Computed.create(this, this.templateWorkspaces, (_use, templates) => {
    const featuredTemplates = flatten((templates).map(t => t.docs)).filter(t => t.isPinned);
    return sortBy(featuredTemplates, t => t.name.toLowerCase());
  });

  public readonly otherSites = Computed.create(this, this.currentPage, this.app.topAppModel.orgs,
    (_use, page, orgs) => {
      if (page !== "all") { return []; }

      const currentOrg = this._app.currentOrg;
      if (!currentOrg) { return []; }

      const isPersonalOrg = currentOrg.owner;
      if (!isPersonalOrg && (currentOrg.access !== "viewers" || !currentOrg.public)) {
        return [];
      }

      return orgs.filter(org => org.id !== currentOrg.id);
    });

  public readonly currentSort: Observable<SortPref>;
  public readonly currentView: Observable<ViewPref>;

  // The workspace for new docs, or "unsaved" to only allow unsaved-doc creation, or null if the
  // user isn't allowed to create a doc.
  public readonly newDocWorkspace = Computed.create(this, this.currentPage, this.currentWS, (use, page, ws) => {
    if (!this.app.currentValidUser) {
      // Anonymous user can create docs, but in unsaved mode and only when enabled.
      return getGristConfig().enableAnonPlayground ? "unsaved" : null;
    }
    if (page === "trash") { return null; }
    const destWS = (["all", "templates", "settings"].includes(page)) ? (use(this.workspaces)[0] || null) : ws;
    return destWS && roles.canEdit(destWS.access) ? destWS : null;
  });

  public readonly empty = Computed.create(this, this.workspaces, (use, wss) => (
    wss.every(ws => ws.isSupportWorkspace || ws.docs.length === 0)));

  public readonly shouldShowAddNewTip = Observable.create(this,
    !this._app.behavioralPromptsManager.hasSeenPopup("addNew"));

  public readonly onboardingTutorial = Observable.create<Document | null>(this, null);

  constructor(private _app: AppModel, clientScope: ClientScope) {
    super();

    if (isKnetEmbed()) {
      this.onlyShowDocuments.set(true);
    }

    // Seed service data from localStorage cache so cards show stale data immediately
    // while the first fetch is in flight.
    const cached = this._dataCache.get();
    if (cached) {
      this.knetAgents.set(cached.knetAgents ?? []);
      this.aperagCollections.set(cached.aperagCollections ?? []);
      this.connectAppConfigs.set(cached.connectAppConfigs ?? []);
      this.connectLinkedAccounts.set(cached.connectLinkedAccounts ?? []);
      this.gisProjects.set(cached.gisProjects ?? []);
      this.aperagBots.set(cached.aperagBots ?? []);
      this.crawlers.set(cached.crawlers ?? []);
    }

    if (!this.app.currentValidUser) {
      // For the anonymous user, use local settings, don't attempt to save anything to the server.
      const viewSettings = makeLocalViewSettings(null, "all");
      this.currentSort = viewSettings.currentSort;
      this.currentView = viewSettings.currentView;
    }
    else {
      // Preference for sorting. Defaults to 'name'. Saved to server on write.
      this.currentSort = Computed.create(this, this._userOrgPrefs,
        (use, prefs) => SortPref.parse(prefs?.docMenuSort) || "name")
        .onWrite(s => this._saveUserOrgPref("docMenuSort", s));

      // Preference for view mode. The default is somewhat complicated. Saved to server on write.
      this.currentView = Computed.create(this, this._userOrgPrefs,
        (use, prefs) => ViewPref.parse(prefs?.docMenuView) || getViewPrefDefault(use(this.workspaces)))
        .onWrite(s => this._saveUserOrgPref("docMenuView", s));
    }

    this.autoDispose(subscribe(this.currentPage, this.currentWSId, use =>
      this.updateWorkspaces().catch(reportError)));

    // Opening a workspace records it as the site's last used one, in the map Kue and the
    // other services on this origin share.
    this.autoDispose(subscribe(this.currentWSId, (use, wsId) => {
      if (wsId === undefined || !this.effectiveOrgId) { return; }
      _writeStorage(LAST_WORKSPACE_KEY,
        recordWorkspace(_readStorage(LAST_WORKSPACE_KEY), this.effectiveOrgId, wsId));
    }));

    // Defer home plugin initialization
    const pluginManager = new HomePluginManager({
      localPlugins: _app.topAppModel.plugins,
      untrustedContentOrigin: _app.topAppModel.getUntrustedContentOrigin(),
      clientScope,
    });
    const importSources = ImportSourceElement.fromArray(pluginManager.pluginsList);
    this.importSources.set(importSources);

    this._app.refreshOrgUsage().catch(reportError);

    this._loadWelcomeTutorial().catch(reportError);
  }

  // Accessor for the AppModel containing this HomeModel.
  public get app(): AppModel { return this._app; }

  public async createWorkspace(name: string) {
    const org = this._app.currentOrg;
    if (!org) { return; }
    this._checkForDuplicates(name);
    await this._app.api.newWorkspace({ name }, org.id);
    await this.updateWorkspaces();
    try {
      const { refreshSession } = await import("app/client/lib/supabaseClient");
      await refreshSession();
    }
    catch (e) {
      console.warn("Failed to refresh Supabase session after workspace creation", e);
    }
  }

  public async renameWorkspace(id: number, name: string) {
    this._checkForDuplicates(name);
    await this._app.api.renameWorkspace(id, name);
    await this.updateWorkspaces();
    try {
      const { refreshSession } = await import("app/client/lib/supabaseClient");
      await refreshSession();
    }
    catch (e) {
      console.warn("Failed to refresh Supabase session after workspace renaming", e);
    }
  }

  public async deleteWorkspace(id: number, forever: boolean) {
    // TODO: Prevent the last workspace from being removed.
    await (forever ? this._app.api.deleteWorkspace(id) : this._app.api.softDeleteWorkspace(id));

    // Await core workspace update so the UI reflects the change (important for navigation)
    await this.updateWorkspaces();

    // Background the Supabase session refresh; it shouldn't block the UI from closing the modal.
    // This is useful after deletion to ensure the JWT reflects that we no longer have access to this resource.
    import("app/client/lib/supabaseClient").then(m => m.refreshSession()).catch((e) => {
      console.warn("Failed to refresh Supabase session after workspace deletion", e);
    });
  }

  public async restoreWorkspace(ws: Workspace) {
    await this._app.api.undeleteWorkspace(ws.id);
    await this.updateWorkspaces();
    try {
      const { refreshSession } = await import("app/client/lib/supabaseClient");
      await refreshSession();
    }
    catch (e) {
      console.warn("Failed to refresh Supabase session after workspace restoration", e);
    }
    reportMessage(`Workspace "${ws.name}" restored`);
  }

  // Creates a new doc by calling the API, and returns its docId.
  public async createDoc(name: string, workspaceId: number | "unsaved"): Promise<string> {
    if (workspaceId === "unsaved") {
      const timezone = await guessTimezone();
      return await this._app.api.newUnsavedDoc({ timezone });
    }
    const id = await this._app.api.newDoc({ name }, workspaceId);
    await this.updateWorkspaces();
    return id;
  }

  public async renameDoc(
    docId: string,
    name: string,
    options?: RenameDocOptions,
  ): Promise<void> {
    await this._app.api.renameDoc(docId, name, options);
    await this.updateWorkspaces();
  }

  public async deleteDoc(docId: string, forever: boolean): Promise<void> {
    await (forever ? this._app.api.deleteDoc(docId) : this._app.api.softDeleteDoc(docId));
    await this.updateWorkspaces();
  }

  public async restoreDoc(doc: Document): Promise<void> {
    await this._app.api.undeleteDoc(doc.id);
    await this.updateWorkspaces();
    reportMessage(`Document "${doc.name
    }" restored`);
  }

  public async pinUnpinDoc(docId: string, pin: boolean): Promise<void> {
    await (pin ? this._app.api.pinDoc(docId) : this._app.api.unpinDoc(docId));
    await this.updateWorkspaces();
  }

  public async pinUnpinItem(item: HomeItem, pin: boolean): Promise<void> {
    // Documents use their own pinning mechanism in the docs table
    if (item.type === "doc" && item.doc) {
      await this.pinUnpinDoc(item.doc.id, pin);
      return;
    }

    // For other types, store in UserOrgPrefs
    const org = this._app.currentOrg;
    if (!org) { return; }

    // Get current pinned items
    const currentPrefs = org.userOrgPrefs || {};
    const pinnedItems = currentPrefs.pinnedItems || {};

    // Determine which array to update based on item type
    let arrayKey: "bots" | "collections" | "knetAgents" | "knetFlows" | "gisProjects" | null = null;
    switch (item.type) {
      case "bot":
        arrayKey = "bots";
        break;
      case "collection":
        arrayKey = "collections";
        break;
      case "knet-agent":
        arrayKey = "knetAgents";
        break;
      case "knet-flow":
        arrayKey = "knetFlows";
        break;
      case "gis-project":
        arrayKey = "gisProjects";
        break;
      default:
        // Unsupported type, do nothing
        return;
    }

    // Update the appropriate array
    const currentArray = pinnedItems[arrayKey] || [];
    let newArray: string[];

    if (pin) {
      // Add to pinned if not already there
      newArray = currentArray.includes(item.id) ? currentArray : [...currentArray, item.id];
    }
    else {
      // Remove from pinned
      newArray = currentArray.filter(id => id !== item.id);
    }

    // Update UserOrgPrefs
    const newPinnedItems = {
      ...pinnedItems,
      [arrayKey]: newArray,
    };

    await this._app.api.updateOrg("current", {
      userOrgPrefs: {
        ...currentPrefs,
        pinnedItems: newPinnedItems,
      },
    });

    // Update local state
    org.userOrgPrefs = {
      ...currentPrefs,
      pinnedItems: newPinnedItems,
    };

    await this.updateWorkspaces();
  }

  /**
   * Cleanup stale pinned items - removes IDs from UserOrgPrefs that no longer exist in backend
   * This runs automatically when loading items and gracefully handles deleted resources
   * IMPORTANT: Only cleans up when the service is ONLINE to avoid removing pins during outages
   *
   * For agents/flows: We now fetch pinned items separately, so if a pinned ID is missing,
   * it means it was actually deleted (not just "not in top 10")
   */
  private _cleanupStalePinnedItems(
    pinnedBots: Set<string>,
    pinnedCollections: Set<string>,
    pinnedKnetAgents: Set<string>,
    pinnedKnetFlows: Set<string>,
    pinnedGisProjects: Set<string>,
    bots: AperagBot[],
    collections: AperagCollection[],
    knetAgents: KnetAgent[],
    knetFlows: KnetFlow[],
    gisProjects: GisProject[],
    userOrgPrefs: UserOrgPrefs | undefined,
  ): void {
    if (!userOrgPrefs?.pinnedItems) { return; }

    // Create sets of existing IDs
    const existingBotIds = new Set(bots.map(b => b.id));
    const existingCollectionIds = new Set(collections.map(c => c.id));
    const existingAgentIds = new Set(knetAgents.map(a => a.id));
    const existingFlowIds = new Set(knetFlows.map(f => f.id));
    const existingGisProjectIds = new Set(gisProjects.map(p => p.id));

    // Check if any pinned IDs are stale, BUT ONLY if the service is online
    // If service is offline, we keep all pinned IDs to avoid data loss
    const staleBots = this._serviceHealth.aperagBotsOnline ?
      Array.from(pinnedBots).filter(id => !existingBotIds.has(id)) :
      [];
    const staleCollections = this._serviceHealth.aperagCollectionsOnline ?
      Array.from(pinnedCollections).filter(id => !existingCollectionIds.has(id)) :
      [];
    const staleAgents = this._serviceHealth.knetAgentsOnline ?
      Array.from(pinnedKnetAgents).filter(id => !existingAgentIds.has(id)) :
      [];
    const staleFlows = this._serviceHealth.knetFlowsOnline ?
      Array.from(pinnedKnetFlows).filter(id => !existingFlowIds.has(id)) :
      [];
    const staleGisProjects = this._serviceHealth.gisProjectsOnline ?
      Array.from(pinnedGisProjects).filter(id => !existingGisProjectIds.has(id)) :
      [];

    // If there are stale items, clean them up
    if (staleBots.length > 0 || staleCollections.length > 0 || staleAgents.length > 0 ||
      staleFlows.length > 0 || staleGisProjects.length > 0) {
      const newPinnedItems = {
        bots: this._serviceHealth.aperagBotsOnline ?
          Array.from(pinnedBots).filter(id => existingBotIds.has(id)) :
          Array.from(pinnedBots), // Keep all if service is down
        collections: this._serviceHealth.aperagCollectionsOnline ?
          Array.from(pinnedCollections).filter(id => existingCollectionIds.has(id)) :
          Array.from(pinnedCollections),
        knetAgents: this._serviceHealth.knetAgentsOnline ?
          Array.from(pinnedKnetAgents).filter(id => existingAgentIds.has(id)) :
          Array.from(pinnedKnetAgents),
        knetFlows: this._serviceHealth.knetFlowsOnline ?
          Array.from(pinnedKnetFlows).filter(id => existingFlowIds.has(id)) :
          Array.from(pinnedKnetFlows),
        gisProjects: this._serviceHealth.gisProjectsOnline ?
          Array.from(pinnedGisProjects).filter(id => existingGisProjectIds.has(id)) :
          Array.from(pinnedGisProjects),
      };

      // Update asynchronously without blocking the UI
      this._app.api.updateOrg("current", {
        userOrgPrefs: {
          ...userOrgPrefs,
          pinnedItems: newPinnedItems,
        },
      }).catch(console.error); // Silently fail if cleanup fails
    }
  }

  public async moveDoc(docId: string, workspaceId: number): Promise<void> {
    await this._app.api.moveDoc(docId, workspaceId);
    await this.updateWorkspaces();
  }

  // Fetches and updates workspaces, which include contained docs as well.
  public async updateWorkspaces() {
    if (this.isDisposed()) {
      return;
    }
    const org = this._app.currentOrg;
    if (!org) {
      this.workspaces.set([]);
      this.trashWorkspaces.set([]);
      this.templateWorkspaces.set([]);
      return;
    }

    this.loading.set(true);
    const currentPage = this.currentPage.get();

    // Get pinned item IDs from preferences to fetch them separately (only
    // agents/flows have pinned-aware fetchers; bots/collections/GIS pins are
    // reconciled in currentWSDocs via _cleanupStalePinnedItems).
    const userOrgPrefs = org.userOrgPrefs;
    const pinnedAgentIds = userOrgPrefs?.pinnedItems?.knetAgents || [];
    const pinnedFlowIds = userOrgPrefs?.pinnedItems?.knetFlows || [];

    // Guard against stale HomeModel firing after org switch: if the URL org no longer matches
    // this org's domain, skip external calls entirely to avoid sending wrong X-Org-ID headers.
    // The merged "docs" org (domain=null/undefined) is exempt since it represents the default org.
    const currentOrgDomain = urlState().state.get().org;
    if (org.domain && currentOrgDomain && org.domain !== currentOrgDomain) {
      // URL has already moved to a different org — this HomeModel is stale, bail out.
      return;
    }

    // Use org.id if valid (non-zero). For the merged "docs" org (id=0), fall back to the
    // selected_org_id cookie which is set server-side by sendAppPage with the real personal org ID.
    // This ensures external service calls (agents, flows, RAG, GIS, etc.) are scoped to the correct
    // org even when getSessionActive() returns id=0 for the merged org.
    const effectiveOrgId = (org.id || _getSelectedOrgIdFromCookie() || org.id);
    console.log("updateWorkspaces: org.id=%s effectiveOrgId=%s cookie=%s",
      org.id, effectiveOrgId, _getSelectedOrgIdFromCookie());

    // ── Phase 1: fetch workspaces (fast, local Grist) ──────────────────
    // Fire these immediately so the doc list and project folders appear without
    // waiting for slow external services (agents, aperag, GIS, connect).
    const corePromise = Promise.all([
      this._fetchWorkspaces(org.id, false).catch(reportError),
      currentPage === "trash" ? this._fetchWorkspaces(org.id, true).catch(reportError) : Promise.resolve(null),
      this._maybeFetchTemplates(),
    ] as const);

    // ── Phase 2: external services — chained off Phase 1 to get workspace IDs ─
    // aperag (bots/collections) and ACI (app-configs/linked-accounts) now require
    // /o/{orgId}/ws/{wsId}/ URL path segments, so we wait for workspaces before calling.
    // Skipped entirely when SKIP_EXTERNAL_SERVICE_FETCHES is set (see the flag
    // comment): every one of these calls wakes a scale-to-zero Knative service.
    const externalPromise = SKIP_EXTERNAL_SERVICE_FETCHES ? null : corePromise.then(([wss]) => {
      const wsIds = (wss || [])
        .filter(w => !w.isSupportWorkspace)
        .map(w => w.id);
      return Promise.all([
        this.app.api.getAperagBots(effectiveOrgId, wsIds)
          .then(r => ({ success: true, data: r })).catch(() => ({ success: false, data: [] as any[] })),
        this.app.api.getAperagCollections(effectiveOrgId, wsIds)
          .then(r => ({ success: true, data: r })).catch(() => ({ success: false, data: [] as any[] })),
        this.app.api.getAperagApiKeys(effectiveOrgId).catch(() => [] as any[]),
        this._fetchConnectResources(effectiveOrgId, wsIds),
        this._fetchKnetAgentsWithPinned(pinnedAgentIds, effectiveOrgId),
        this._fetchKnetFlowsWithPinned(pinnedFlowIds, effectiveOrgId),
        this.app.api.getGisProjects(effectiveOrgId)
          .then(r => ({ success: true, data: r })).catch(() => ({ success: false, data: [] as any[] })),
        this.app.api.getCrawlers(effectiveOrgId, wsIds)
          .then(r => ({ success: true, data: r })).catch(() => ({ success: false, data: [] as any[] })),
      ] as const);
    });

    // Show "slow" spinner on cards only if external services are slow
    const slowTimer = externalPromise && isLongerThan(externalPromise, DELAY_BEFORE_SPINNER_MS).then((slow) => {
      if (slow && !this.isDisposed()) { this.loading.set("slow"); }
    });

    // ── Await Phase 1: make doc list / project folders appear immediately ─────
    const [wss, trashWss, templateWss] = await corePromise;
    if (this.isDisposed()) { return; }

    bundleChanges(() => {
      this.workspaces.set(wss || []);
      this.trashWorkspaces.set(trashWss || []);
      this.templateWorkspaces.set(templateWss || []);
      this.available.set(!!wss);
      const nonSupportWss = Array.isArray(wss) ? wss.filter(ws => !ws.isSupportWorkspace) : null;
      this.singleWorkspace.set(
        nonSupportWss?.length === 0 ||
        (nonSupportWss?.length === 1 && _isSingleWorkspaceMode(this._app)),
      );
    });

    // External fetches disabled: run off the localStorage cache seeded in the
    // constructor. Mark every service as not-fetched so _cleanupStalePinnedItems
    // never prunes pinned items against cache-only data.
    if (!externalPromise) {
      this._serviceHealth = {
        aperagBotsOnline: false,
        aperagCollectionsOnline: false,
        knetAgentsOnline: false,
        knetFlowsOnline: false,
        gisProjectsOnline: false,
        crawlersOnline: false,
      };
      this.loading.set(false);
      return;
    }

    // ── Phase 2: Handle external-service data in the background ───────────────
    // We don't await this here so that the caller (e.g. deleteWorkspace) can
    // proceed as soon as the core Grist UI is updated and navigation can happen.
    externalPromise.then(([botsResult, collectionsResult, apiKeys, connectData,
      agentsResult, flowsResult, gisProjectsResult, crawlersResult]) => {
      void slowTimer; // already running; just ensure no unhandled rejection
      if (this.isDisposed()) { return; }

      const serviceHealth = {
        aperagBotsOnline: botsResult.success,
        aperagCollectionsOnline: collectionsResult.success,
        configuredAppsOnline: connectData.success,
        knetAgentsOnline: agentsResult.success,
        knetFlowsOnline: flowsResult.success,
        gisProjectsOnline: gisProjectsResult.success,
        crawlersOnline: crawlersResult.success,
      };

      bundleChanges(() => {
        // Only overwrite data when the service responded successfully — preserve last-known
        // data when a service is sleeping/offline so the UI can still show stale counts.
        if (serviceHealth.aperagBotsOnline) { this.aperagBots.set(botsResult.data); }
        if (serviceHealth.aperagCollectionsOnline) { this.aperagCollections.set(collectionsResult.data); }
        this.aperagApiKeys.set(apiKeys || []);
        if (serviceHealth.configuredAppsOnline) {
          this.connectProjects.set(connectData?.projects || []);
          this.connectAppConfigs.set(connectData?.appConfigs || []);
          this.connectLinkedAccounts.set(connectData?.linkedAccounts || []);
        }
        if (serviceHealth.knetAgentsOnline) { this.knetAgents.set(agentsResult.data); }
        if (serviceHealth.knetFlowsOnline) { this.knetFlows.set(flowsResult.data); }
        if (serviceHealth.gisProjectsOnline) { this.gisProjects.set(gisProjectsResult.data); }
        if (serviceHealth.crawlersOnline) { this.crawlers.set(crawlersResult.data); }

        // Store service health for cleanup logic
        this._serviceHealth = serviceHealth;
        // Persist a snapshot of successfully-fetched service data to localStorage
        this._dataCache.set({
          fetchedAt: new Date().toISOString(),
          knetAgents: this.knetAgents.get(),
          aperagCollections: this.aperagCollections.get(),
          connectAppConfigs: this.connectAppConfigs.get(),
          connectLinkedAccounts: this.connectLinkedAccounts.get(),
          gisProjects: this.gisProjects.get(),
          aperagBots: this.aperagBots.get(),
          crawlers: this.crawlers.get(),
        });
        // Update public observables for UI
        this.aperagBotsOnline.set(serviceHealth.aperagBotsOnline);
        this.aperagCollectionsOnline.set(serviceHealth.aperagCollectionsOnline);
        this.knetAgentsOnline.set(serviceHealth.knetAgentsOnline);
        this.knetFlowsOnline.set(serviceHealth.knetFlowsOnline);
        this.gisProjectsOnline.set(serviceHealth.gisProjectsOnline);
        this.configuredAppsOnline.set(serviceHealth.configuredAppsOnline);
        this.crawlersOnline.set(serviceHealth.crawlersOnline);

        // Finalize loading state
        this.loading.set(false);
      });
    }).catch((err) => {
      console.warn("HomeModel: Phase 2 update failed", err);
      if (!this.isDisposed()) { this.loading.set(false); }
    });
  }

  /**
   * Fetch Knet agents: top 10 recent + any pinned items not in top 10
   * This ensures pinned items are always included even if they're older
   */
  private async _fetchKnetAgentsWithPinned(pinnedIds: string[], orgId: number):
  Promise<{ success: boolean; data: KnetAgent[]; hasPinned: boolean }> {
    try {
      // Get top 10 recent agents, scoped to current org
      const recentAgents = await this.app.api.getKnetAgents(10, orgId);

      // If no pinned items, just return recent
      if (pinnedIds.length === 0) {
        return { success: true, data: recentAgents, hasPinned: false };
      }

      // Check which pinned IDs are NOT in the recent list
      const recentIds = new Set(recentAgents.map(a => a.id));
      const missingPinnedIds = pinnedIds.filter(id => !recentIds.has(id));

      // If all pinned items are already in recent, we're done
      if (missingPinnedIds.length === 0) {
        return { success: true, data: recentAgents, hasPinned: true };
      }

      // Fetch the missing pinned agents individually
      // Note: This assumes there's a way to fetch by ID. If not, we'll just use recent list
      // and NOT clean up pinned items (they might exist but not be fetchable)
      const pinnedAgents = await Promise.all(
        missingPinnedIds.map(id =>
          this.app.api.getKnetAgents(100, orgId).then(all => all.find(a => a.id === id)).catch(() => undefined),
        ),
      );

      // Merge recent + pinned (filter out undefined)
      const merged = [...recentAgents, ...pinnedAgents.filter((a): a is KnetAgent => a !== undefined)];

      return { success: true, data: merged, hasPinned: true };
    }
    catch (error) {
      return { success: false, data: [], hasPinned: pinnedIds.length > 0 };
    }
  }

  /**
   * Fetch Knet flows: top 10 recent + any pinned items not in top 10
   */
  private async _fetchKnetFlowsWithPinned(pinnedIds: string[], orgId: number):
  Promise<{ success: boolean; data: KnetFlow[]; hasPinned: boolean }> {
    try {
      // Get top 10 recent flows, scoped to current org (backend orders by updated_at desc)
      const recentFlows = await this.app.api.getKnetFlows(10, orgId);

      // If no pinned items, just return recent
      if (pinnedIds.length === 0) {
        return { success: true, data: recentFlows, hasPinned: false };
      }

      // Check which pinned IDs are NOT in the recent list
      const recentIds = new Set(recentFlows.map(f => f.id));
      const missingPinnedIds = pinnedIds.filter(id => !recentIds.has(id));

      // If all pinned items are already in recent, we're done
      if (missingPinnedIds.length === 0) {
        return { success: true, data: recentFlows, hasPinned: true };
      }

      // Fetch a wider page to find missing pinned flows
      const allFlows = await this.app.api.getKnetFlows(100, orgId);
      const missingPinned = allFlows.filter(f => missingPinnedIds.includes(f.id));
      const merged = [...recentFlows, ...missingPinned];

      return { success: true, data: merged, hasPinned: true };
    }
    catch (error) {
      return { success: false, data: [], hasPinned: pinnedIds.length > 0 };
    }
  }

  private _checkForDuplicates(name: string): void {
    if (this.workspaces.get().find(ws => ws.name === name)) {
      throw new UserError("Name already exists. Please choose a different name.");
    }
  }

  // _fetchConnectResources: fetches app-configurations and linked-accounts from ACI backend.
  // ACI uses /o/{orgId}/ws/{wsId}/ URL path routing — one call per workspace.
  private async _fetchConnectResources(orgId: number, wsIds: number[]) {
    try {
      const [appConfigs, linkedAccounts] = await Promise.all([
        this.app.api.getConnectAppConfigs(orgId, wsIds).catch(() => [] as ConnectAppConfig[]),
        this.app.api.getConnectLinkedAccounts(orgId, wsIds).catch(() => [] as ConnectLinkedAccount[]),
      ]);

      return {
        success: true,
        projects: [],
        appConfigs,
        linkedAccounts,
      };
    }
    catch (e) {
      console.error("HomeModel._fetchConnectResources: error fetching connect resources", e);
      return { success: false, projects: [], appConfigs: [], linkedAccounts: [] };
    }
  }

  private async _fetchWorkspaces(orgId: number, forRemoved: boolean) {
    let api = this._app.api;
    if (forRemoved) {
      api = api.forRemoved();
    }
    const wss = await api.getOrgWorkspaces(orgId);
    if (this.isDisposed()) { return null; }
    for (const ws of wss) {
      ws.docs = sortBy(ws.docs, doc => doc.name.toLowerCase());

      // Populate doc.removedAt for soft-deleted docs even when deleted along with a workspace.
      if (forRemoved) {
        for (const doc of ws.docs) {
          doc.removedAt = doc.removedAt || ws.removedAt;
        }
      }

      // Populate doc.workspace, which is used by DocMenu/PinnedDocs and
      // is useful in cases where there are multiple workspaces containing
      // pinned documents that need to be sorted in alphabetical order.
      for (const doc of ws.docs) {
        doc.workspace = doc.workspace ?? ws;
      }
    }
    // Sort workspaces such that workspaces from the personal orgs of others
    // come after workspaces from our own personal org; workspaces from personal
    // orgs are grouped by personal org and the groups are ordered alphabetically
    // by owner name; and all else being equal workspaces are ordered alphabetically
    // by their name.  All alphabetical ordering is case-insensitive.
    // Workspaces shared from support account (e.g. samples) are put last.
    return sortBy(wss, ws => [ws.isSupportWorkspace,
      ownerName(this._app, ws).toLowerCase(),
      ws.name.toLowerCase()]);
  }

  /**
   * Fetches templates if on the Templates page.
   */
  private async _maybeFetchTemplates(): Promise<Workspace[] | null> {
    if (!getGristConfig().templateOrg || this.currentPage.get() !== "templates") {
      return null;
    }

    let templateWss: Workspace[] = [];
    try {
      templateWss = await this._app.api.getTemplates();
    }
    catch {
      reportError("Failed to load templates");
    }
    if (this.isDisposed()) { return null; }

    for (const ws of templateWss) {
      for (const doc of ws.docs) {
        // Populate doc.workspace, which is used by DocMenu/PinnedDocs and
        // is useful in cases where there are multiple workspaces containing
        // pinned documents that need to be sorted in alphabetical order.
        doc.workspace = doc.workspace ?? ws;
      }
      ws.docs = sortBy(ws.docs, doc => doc.name.toLowerCase());
    }
    return templateWss;
  }

  private async _loadWelcomeTutorial() {
    const { templateOrg, onboardingTutorialDocId } = getGristConfig();
    if (
      !isFeatureEnabled("tutorials") ||
      !templateOrg ||
      !onboardingTutorialDocId
    ) {
      return;
    }

    try {
      const doc = await this._app.api.getTemplate(onboardingTutorialDocId);
      if (this.isDisposed()) { return; }

      this.onboardingTutorial.set(doc);
    }
    catch (e) {
      console.error(e);
      reportError("Failed to load welcome tutorial");
    }
  }

  private async _saveUserOrgPref<K extends keyof UserOrgPrefs>(key: K, value: UserOrgPrefs[K]) {
    const org = this._app.currentOrg;
    if (org) {
      org.userOrgPrefs = { ...org.userOrgPrefs, [key]: value };
      this._userOrgPrefs.set(org.userOrgPrefs);
      await this._app.api.updateOrg("current", { userOrgPrefs: org.userOrgPrefs });
    }
  }
}

// Reads the selected_org_id cookie, which the server sets to indicate the active org for
// external services. Used as fallback when org.id is 0 (e.g. personal org).
// Format raw app_name (e.g. "KNET_DATABASES") to title case with spaces (e.g. "Knet Databases").
function _formatAppName(name: string | undefined): string {
  if (!name) { return ""; }
  return name.replace(/_/g, " ").replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

function _getSelectedOrgIdFromCookie(): number | undefined {
  if (typeof document === "undefined") { return undefined; }
  const match = document.cookie.match(/(?:^|;\s*)selected_org_id=(\d+)/);
  const val = match ? parseInt(match[1], 10) : undefined;
  return val && val > 0 ? val : undefined;
}

// Check if active product allows just a single workspace.
function _isSingleWorkspaceMode(app: AppModel): boolean {
  return app.currentFeatures?.maxWorkspacesPerOrg === 1;
}

// Returns a default view mode preference. We used to show 'list' for everyone. We now default to
// 'icons' for new or light users. But if a user has more than 4 docs or any pinned docs, we'll
// switch to 'list'. This will also avoid annoying existing users who may prefer a list.
function getViewPrefDefault(workspaces: Workspace[]): ViewPref {
  const userWorkspaces = workspaces.filter(ws => !ws.isSupportWorkspace);
  const numDocs = userWorkspaces.reduce((sum, ws) => sum + ws.docs.length, 0);
  const pinnedDocs = userWorkspaces.some(ws => ws.docs.some(doc => doc.isPinned));
  return (numDocs > 4 || pinnedDocs) ? "list" : "icons";
}

/**
 * Create observables for per-workspace view settings which default to org-wide settings, but can
 * be changed independently and persisted in localStorage.
 */
export function makeLocalViewSettings(
  home: HomeModel | null, wsId: number | "trash" | "all" | "templates",
): ViewSettings {
  const userId = home?.app.currentUser?.id || 0;
  const sort = localStorageObs(`u=${userId}:ws=${wsId}:sort`);
  const view = localStorageObs(`u=${userId}:ws=${wsId}:view`);

  return {
    currentSort: Computed.create(null,
      // If no value in localStorage, use sort of All Documents.
      use => SortPref.parse(use(sort)) || (home ? use(home.currentSort) : "name"))
      .onWrite(val => sort.set(val)),
    currentView: Computed.create(null,
      // If no value in localStorage, use mode of All Documents, except Trash which defaults to 'list'.
      use => ViewPref.parse(use(view)) || (wsId === "trash" ? "list" : (home ? use(home.currentView) : "icons")))
      .onWrite(val => view.set(val)),
  };
}

function _readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  }
  catch {
    return null;
  }
}

function _writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  }
  catch {
    // Private mode or blocked storage: the choice just isn't remembered.
  }
}
